import { useEffect, useRef, useState } from 'react';

/** Tempo mínimo na tela, mesmo se a sessão resolver antes disso. */
const MIN_MS = 3_000;
const EXIT_MS = 220;

/**
 * Tela de abertura, uma vez por carregamento do app.
 *
 * `ready` chega true quando a sessão já resolveu. O componente só some
 * quando as duas coisas acontecerem — sessão pronta E tempo mínimo
 * cumprido — para nunca piscar numa rede rápida nem prender numa lenta
 * além do necessário.
 */
export function Splash({
  role,
  tagline,
  ready,
  onFinished,
}: {
  /** "Motorista" ou "Passageiro" — a marca (WiGo) é fixa, isto varia. */
  role: string;
  tagline: string;
  ready: boolean;
  onFinished: () => void;
}) {
  const [exiting, setExiting] = useState(false);
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    if (!ready || exiting) return;
    const elapsed = Date.now() - mountedAt.current;
    const wait = Math.max(0, MIN_MS - elapsed);
    const timer = setTimeout(() => setExiting(true), wait);
    return () => clearTimeout(timer);
  }, [ready, exiting]);

  useEffect(() => {
    if (!exiting) return;
    const timer = setTimeout(onFinished, EXIT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exiting]);

  return (
    <div
      // Hex fixo, não var(--c-brand): são as mesmas três cores em
      // qualquer tema (brand.css nunca as redefine no bloco escuro), e
      // o splash é a primeiríssima coisa a pintar na tela — não vale o
      // risco de rodar antes do CSS da marca ter terminado de carregar.
      className="fixed inset-0 z-50 grid place-items-center bg-[linear-gradient(165deg,#ff8f3d,#ff5a1f_55%,#c2360a)] transition-opacity ease-out"
      style={{
        opacity: exiting ? 0 : 1,
        transitionDuration: `${EXIT_MS}ms`,
      }}
      role="status"
      aria-label={`Abrindo WiGo ${role}`}
    >
      <div className="flex flex-col items-center">
        {/* Só o símbolo — a marca escrita fica para a tela de login, que
            tem espaço horizontal de sobra. Aqui, repetir "WiGo" ao lado
            do próprio símbolo da marca seria dizer a mesma coisa duas
            vezes na mesma tela. */}
        {/* Sem drop-shadow aqui: a imagem já desenha a própria sombra —
            o "chão" ovalado embaixo do pino. Um filtro de sombra por
            cima repetia o efeito e virava um borrão atrás do símbolo. */}
        <img
          // ?v= porque o Apache serve .png com cache immutable de um
          // ano — sem isso, quem já abriu o app antes nunca rebusca a
          // imagem nova mesmo com o arquivo trocado no servidor.
          src="/brand-pin.png?v=2"
          alt=""
          aria-hidden="true"
          width={136}
          height={162}
          className="h-40 w-auto [animation:marker-arrive_.5s_cubic-bezier(.2,.8,.3,1.3)_both]"
        />

        <p className="eyebrow mt-5 text-brand-ink/80 [animation:splash-fade-in_.5s_.24s_ease-out_both]">
          {role}
        </p>
        <p className="mt-2.5 text-sm font-medium text-brand-ink/70 [animation:splash-fade-in_.5s_.36s_ease-out_both]">
          {tagline}
        </p>
      </div>
    </div>
  );
}
